// Authentication service handling OpenID Connect (OIDC) integration with PKCE flow,
// including automatic silent renew, axios bearer-token sync, and SSO redirect handling.
// Exports a provider and a custom hook used by other common code. Application feature
// code should not need to use this provider directly as authentication is handled in
// the background, allowing the application to assume the current user is authenticated
// (but not necessarily authorized, which the application checks with the Users service).
//
// Uses react-oidc-context as the React integration layer and oidc-client-ts as the
// underlying OIDC implementation. The react-oidc-context package provides React context
// and hooks around oidc-client-ts, while the oidc-client-ts package handles protocol
// details such as PKCE, callback processing, token storage, and silent renew.
// https://github.com/authts/react-oidc-context#documentation
// https://authts.github.io/oidc-client-ts
//
// OIDC configuration for the SSO provider and environment the application is running
// in comes from RUNTIME_CONFIG settings oidc.issuerUri, oidc.clientId, and oidc.scope.
// Missing runtime settings fail fast with a configuration error. The PUBLIC_URL
// environment variable controls the redirect URI base path.
//
// The SSO provider redirects back with temporary callback parameters such as code,
// state, session_state, iss, error, error_description, or error_uri.
// react-oidc-context processes those parameters, and onSigninCallback removes them
// from the URL so silent renew can continue working.
//
// Tokens are refreshed by oidc-client-ts automatic silent renew. This provider syncs
// the current access token to axios and removes it when no user is available. If renewal
// fails, this provider tries one explicit silent recovery before restarting SSO.
//
// OIDC request state and authenticated user state are stored in sessionStorage. A
// hard reload in the same tab can reuse a valid stored OIDC user, while a closed tab
// does not persist the authentication state. There is no session-expired page or
// explicit logout flow, as these are not currently needed by applications.
//
// Startup/login flow:
// - Trigger: react-oidc-context finishes initialization/callback processing and this
//   provider sees no authenticated user.
// - If the URL has OIDC callback params, react-oidc-context processes them and
//   onSigninCallback removes them from the URL.
// - If a valid user exists in sessionStorage or from the callback, axios receives the
//   access token and the application renders.
// - If no authenticated user exists after initialization/callback handling, this
//   provider calls signinRedirect() and the browser leaves the app for SSO.
//
// Token recovery flow:
// - Trigger: oidc-client-ts emits access-token-expired or silent-renew-error.
// - This provider tries one signinSilent() recovery without leaving the app.
// - If silent recovery returns a valid user, axios receives the refreshed token and
//   the application continues.
// - If silent recovery fails or returns no valid user, this provider removes local
//   OIDC user state. That unauthenticated state triggers the startup/login flow,
//   which calls signinRedirect() to restart SSO.

import axios from "axios";
import { WebStorageStateStore } from "oidc-client-ts";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  AuthProvider as OidcAuthProvider,
  hasAuthParams,
  useAuth as useOidcAuth,
} from "react-oidc-context";

// Build the application callback URL from the current origin and optional create-react-app base path
const getOidcRedirectUri = () =>
  new URL(process.env.PUBLIC_URL || "/", window.location.origin).toString();

// Create a per-tab OIDC storage adapter for request state and authenticated user state
const createSessionStorageStore = () =>
  new WebStorageStateStore({ store: window.sessionStorage });

// Remove temporary OIDC callback parameters after react-oidc-context processes them
const removeAuthCallbackParams = () => {
  const url = new URL(window.location.href);

  url.searchParams.delete("code");
  url.searchParams.delete("state");
  url.searchParams.delete("session_state");
  url.searchParams.delete("iss");
  url.searchParams.delete("error");
  url.searchParams.delete("error_description");
  url.searchParams.delete("error_uri");

  window.history.replaceState(
    {},
    document.title,
    `${url.pathname}${url.search}${url.hash}`,
  );
};

// Create the OIDC configuration object from runtime settings and typical defaults
const createOidcConfig = () => {
  const oidc = window.RUNTIME_CONFIG.oidc;

  if (!oidc?.issuerUri || !oidc?.clientId || !oidc?.scope) {
    throw new Error(
      "OIDC configuration is missing or incomplete in window.RUNTIME_CONFIG. " +
        "Ensure public/env.js is properly loaded and contains the oidc configuration object.",
    );
  }

  return {
    authority: oidc.issuerUri,
    client_id: oidc.clientId,
    redirect_uri: getOidcRedirectUri(),
    response_type: "code",
    scope: oidc.scope,
    refreshTokenAllowedScope: oidc.scope,
    automaticSilentRenew: true,
    loadUserInfo: false,
    monitorSession: false,
    revokeTokensOnSignout: false,
    stateStore: createSessionStorageStore(),
    userStore: createSessionStorageStore(),
    onSigninCallback: removeAuthCallbackParams,
  };
};

// Attach the current access token to axios requests
const setAuthorizationHeader = (accessToken) => {
  axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
};

// Clear the axios bearer token when auth state no longer has an access token
const removeAuthorizationHeader = () => {
  delete axios.defaults.headers.common.Authorization;
};

// Keep axios and SSO redirect behavior synchronized with the OIDC auth lifecycle
const AuthLifecycle = ({ children }) => {
  const {
    activeNavigator,
    error,
    events,
    isAuthenticated,
    isLoading,
    removeUser,
    signinRedirect,
    signinSilent,
    user,
  } = useOidcAuth();
  const redirectInProgressRef = useRef(false);
  const recoveryInProgressRef = useRef(false);

  // Set the axios token before child useEffect API calls run, including the initial users/me request
  useLayoutEffect(() => {
    if (user?.access_token) {
      setAuthorizationHeader(user.access_token);
      return;
    }

    removeAuthorizationHeader();
  }, [user?.access_token]);

  // Track redirect state and send unauthenticated users to SSO after initialization
  useEffect(() => {
    // A valid session means any previous redirect attempt is complete
    if (isAuthenticated) {
      redirectInProgressRef.current = false;
      return;
    }

    const isProcessingAuthCallback = hasAuthParams() && !error;

    // Wait until auth startup/callback/navigation/recovery work has settled
    if (
      isProcessingAuthCallback ||
      isLoading ||
      activeNavigator ||
      isAuthenticated ||
      recoveryInProgressRef.current ||
      redirectInProgressRef.current
    ) {
      return;
    }

    redirectInProgressRef.current = true;

    // Clear failed callback params before redirecting so they are not replayed
    if (error) {
      console.error("OIDC authentication failed; redirecting to SSO.", error);
      removeAuthCallbackParams();
    } else {
      console.error("OIDC authentication is required; redirecting to SSO.");
    }

    // Hand control to the external SSO provider for interactive sign-in
    void signinRedirect()
      .then((redirectResult) => {
        // react-oidc-context returns null when the redirect call fails before navigation
        if (redirectResult === null) {
          redirectInProgressRef.current = false;
        }
      })
      .catch((redirectError) => {
        redirectInProgressRef.current = false;
        console.error("OIDC authentication redirect failed.", redirectError);
      });
  }, [activeNavigator, error, isAuthenticated, isLoading, signinRedirect]);

  // On token-renewal failure, try one silent recovery before restarting SSO
  useEffect(() => {
    // Give oidc-client-ts one explicit signinSilent attempt before clearing the local user
    const recoverAuthentication = async (message, recoveryReason) => {
      if (recoveryInProgressRef.current || redirectInProgressRef.current) {
        return;
      }

      recoveryInProgressRef.current = true;
      console.error(message, recoveryReason);

      // First ask oidc-client-ts to recover the session without leaving the app
      try {
        const recoveredUser = await signinSilent();

        if (recoveredUser?.access_token && !recoveredUser.expired) {
          recoveryInProgressRef.current = false;
          return;
        }

        console.error(
          "OIDC silent recovery did not return a valid user; restarting SSO.",
        );
      } catch (recoveryError) {
        console.error(
          "OIDC silent recovery failed; restarting SSO.",
          recoveryError,
        );
      }

      // If silent recovery failed, clear local auth state so normal SSO can restart
      try {
        await removeUser();
      } catch (removeUserError) {
        console.error("OIDC user cleanup failed before SSO.", removeUserError);
      } finally {
        recoveryInProgressRef.current = false;
      }
    };

    // Register OIDC event listeners that must be removed when React cleans up this effect
    const removeEventListeners = [
      events.addAccessTokenExpired(() => {
        void recoverAuthentication(
          "OIDC access token expired; attempting silent recovery.",
        );
      }),
      events.addSilentRenewError((renewError) => {
        void recoverAuthentication(
          "OIDC token refresh failed; attempting silent recovery.",
          renewError,
        );
      }),
    ];

    // Remove registered OIDC event listeners before this effect runs again or unmounts
    return () => {
      removeEventListeners.forEach((removeEventListener) => {
        removeEventListener();
      });
    };
  }, [events, removeUser, signinSilent]);

  return children;
};

// Context provider for the OIDC state and authentication lifecycle
export const AuthProvider = ({ children }) => {
  const oidcConfig = useMemo(createOidcConfig, []);

  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthLifecycle>{children}</AuthLifecycle>
    </OidcAuthProvider>
  );
};

// Custom hook to access the context
export const useAuth = () => {
  return useOidcAuth();
};
